import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const HEADER_OFFSET = 100;

/**
 * SmartLink - A link component that handles both page navigation and smooth scrolling
 * 
 * Features:
 * - Routes to different pages correctly
 * - Handles hash links with smooth scrolling
 * - Works for same-page and cross-page hash links
 * - Accounts for fixed header offset
 */
export const SmartLink = ({ 
  to, 
  children, 
  className = '', 
  onClick,
  ...props 
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleClick = (e) => {
    // Call any custom onClick handler
    if (onClick) {
      onClick(e);
    }

    // Parse the destination
    const hasHash = to.includes('#');
    
    if (!hasHash) {
      // No hash - let React Router handle it normally
      return;
    }

    const [pathname, hash] = to.split('#');
    const targetPath = pathname || location.pathname;
    const issamePage = targetPath === location.pathname || pathname === '';

    if (issamePage && hash) {
      // Same page hash link - prevent default and scroll
      e.preventDefault();
      
      const element = document.getElementById(hash);
      if (element) {
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - HEADER_OFFSET;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });

        // Update URL without triggering navigation
        window.history.pushState(null, '', `${location.pathname}#${hash}`);
      }
    }
    // Cross-page hash links will be handled by ScrollToTop in App.js
  };

  return (
    <Link 
      to={to} 
      className={className} 
      onClick={handleClick}
      {...props}
    >
      {children}
    </Link>
  );
};

/**
 * ScrollButton - A button that scrolls to a section on the current page
 */
export const ScrollButton = ({
  targetId,
  children,
  className = '',
  onClick,
  ...props
}) => {
  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    }

    const element = document.getElementById(targetId);
    if (element) {
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - HEADER_OFFSET;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <button
      type="button"
      className={className}
      onClick={handleClick}
      {...props}
    >
      {children}
    </button>
  );
};

export default SmartLink;
